import React, {
  type FC,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import classnames from 'classnames';
import { Trans } from 'react-i18next';
import { Slide, Slider } from 'pure-react-carousel';

import { useRWD } from 'hooks';
import { useGetProductsBoughtByOthers, useGetProductsBoughtByOthersMutation } from 'api';
import { Loader } from 'components/controls';

import WiseCarousel from 'pages/wise/WiseProduct/components/WiseCarousel';
import WiseProductItem from 'pages/wise/WiseProduct/components/WiseProductItem';

import styles from 'theme/pages/wise/WiseProduct/components/WiseOthersAlsoBought/WiseOthersAlsoBought.module.scss';

const WISE_PRODUCT_CARD_WIDTH_PX = 260;
const CAROUSEL_MAX_VISIBLE_SLIDES = 5;
const SLIDE_GAP_PX = 8;
const WISE_PRODUCT_CARD_HEIGHT_PX = 465;

function countCardsThatFitRow(widthPx: number): number {
  if (widthPx <= 0) {
    return 1;
  }
  const slot = WISE_PRODUCT_CARD_WIDTH_PX + SLIDE_GAP_PX;
  return Math.max(1, Math.floor((widthPx + SLIDE_GAP_PX) / slot));
}

export type WiseOthersAlsoBoughtProps = {
  productId: number;
  showNavArrows?: boolean;
};

const WiseOthersAlsoBought: FC<WiseOthersAlsoBoughtProps> = ({ productId, showNavArrows }) => {
  const { isMobile } = useRWD();
  const rootRef = useRef<HTMLDivElement>(null);
  const [fitsInRow, setFitsInRow] = useState<number | null>(null);

  const { data: productsBoughtByOthersSimpleData, isLoading: isProductsBoughtByOthersLoading } =
    useGetProductsBoughtByOthers(
      productId,
      {
        page: 1,
        limit: 999
      },
      {
        onSuccess: (data) => {
          refetchProductsFullData({
            scopes: 'PriceProductData',
            productIds: data.items
              .map((item) => item.id)
              .toString()
              .replaceAll(',', '|'),
            page: 1,
            limit: 999
          });
        }
      }
    );

  const { data: productsBoughtByOthersFullData, mutate: refetchProductsFullData } =
    useGetProductsBoughtByOthersMutation(productId);

  const mergedItems = useMemo(() => {
    return productsBoughtByOthersSimpleData?.items?.map((simpleProduct) => {
      return productsBoughtByOthersFullData
        ? {
            ...simpleProduct,
            ...productsBoughtByOthersFullData?.items.find(
              (fullProduct) => fullProduct.id === simpleProduct.id
            )
          }
        : simpleProduct;
    });
  }, [productsBoughtByOthersSimpleData, productsBoughtByOthersFullData]);

  const itemCount = productsBoughtByOthersSimpleData?.items?.length ?? 0;
  const resolvedTitle = <Trans>Inni kupili także</Trans>;
  const resolvedShowArrows = showNavArrows ?? !isMobile;

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return;
    }

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setFitsInRow(countCardsThatFitRow(w));
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [itemCount]);

  /** Karuzela tylko gdy jest więcej produktów niż mieści się w jednym rzędzie (`fitsInRow + 1` i więcej). */
  const useCarousel =
    fitsInRow === null
      ? itemCount > CAROUSEL_MAX_VISIBLE_SLIDES
      : itemCount > fitsInRow;

  const visibleSlidesInCarousel = isMobile
    ? 1
    : Math.min(
        CAROUSEL_MAX_VISIBLE_SLIDES,
        itemCount,
        Math.max(1, fitsInRow ?? CAROUSEL_MAX_VISIBLE_SLIDES)
      );

  const renderProductItems = () =>
    mergedItems?.map((product) => {
      const price = product.product_price_short_info?.prices?.find(
        (p) => p.unit_id === product.product_unit_short_info?.default_unit_id || 0
      );

      return (
        <WiseProductItem key={product.id} product={product} price={price ?? null} />
      );
    });

  return (
    <div
      ref={rootRef}
      className={classnames(
        styles.wiseOthersRoot,
        'StylePath-Pages-Wise-WiseProduct-components-WiseOthersAlsoBought'
      )}>
      {isProductsBoughtByOthersLoading && <Loader />}

      {itemCount > 0 && !useCarousel && (
        <div className={styles.inlineBlock}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>{resolvedTitle}</div>
          </div>
          <div className={styles.inlineRow}>{renderProductItems()}</div>
        </div>
      )}

      {itemCount > 0 && useCarousel && (
        <WiseCarousel
          totalSlides={itemCount}
          visibleSlides={visibleSlidesInCarousel}
          naturalSlideWidth={WISE_PRODUCT_CARD_WIDTH_PX}
          naturalSlideHeight={WISE_PRODUCT_CARD_HEIGHT_PX}
          title={resolvedTitle}
          showNavArrows={resolvedShowArrows}
          disableKeyboard>
          <Slider>
            {mergedItems?.map((product, index) => {
              const price = product.product_price_short_info?.prices?.find(
                (p) => p.unit_id === product.product_unit_short_info?.default_unit_id || 0
              );

              return (
                <Slide key={product.id} index={index} className={styles.slide}>
                  <div className={styles.productWrapper}>
                    <WiseProductItem product={product} price={price ?? null} />
                  </div>
                </Slide>
              );
            })}
          </Slider>
        </WiseCarousel>
      )}
    </div>
  );
};

export default WiseOthersAlsoBought;
